import { z } from 'zod';

import { OBRA_STATUSES, type ObraStatus, type VisitStatus } from '../domain.js';
import { csvEnumSchema, csvIdsSchema, dateOnlySchema } from './common.js';

/**
 * Recorte do analítico.
 *
 * O período filtra **movimentação**, não cadastro: "de janeiro a março" quer
 * dizer o que foi medido, aditivado ou glosado nesse intervalo, não as obras
 * criadas nele. Os KPIs de saldo continuam sendo a posição atual da obra, que
 * não tem recorte de tempo — quem tenta recortar saldo por período acaba
 * somando estado com fluxo.
 */
export const analyticsQuerySchema = z.object({
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  fiscalIds: csvIdsSchema,
  companyIds: csvIdsSchema,
  /* Quem demandou a obra. Responde "quanto a Educação tem em execução". */
  secretariaIds: csvIdsSchema,
  statuses: csvEnumSchema(OBRA_STATUSES),
});
export type AnalyticsQuery = z.infer<typeof analyticsQuerySchema>;

/** Posição do acervo no recorte. Valores em centavos. */
export interface AnalyticsTotals {
  readonly obras: number;
  readonly contractedCents: number;
  readonly paidCents: number;
  readonly glossCents: number;
  /** Contratado menos pago. Pode ser zero, nunca negativo. */
  readonly remainingCents: number;
  /** Fração paga sobre o contratado, em [0, 1]. */
  readonly executionRatio: number;
}

/** Quanto se moveu no período, por natureza do lançamento. */
export interface AnalyticsFlow {
  readonly measuredCents: number;
  readonly additionsCents: number;
  readonly suppressionsCents: number;
  readonly glossCents: number;
  readonly reports: number;
}

/** Um mês da série. `month` é `yyyy-mm`. */
export interface AnalyticsMonth {
  readonly month: string;
  readonly measuredCents: number;
  readonly additionsCents: number;
  readonly glossCents: number;
}

/** Como anda o cumprimento de prazo dentro do recorte. */
export interface AnalyticsDeadlines {
  readonly overdueVisits: number;
  readonly dueSoonVisits: number;
  readonly paused: number;
  /** Obras cujo prazo de contrato já venceu sem conclusão. */
  readonly expiredContracts: number;
  /** Dias somados de todas as paralisações abertas no recorte. */
  readonly pausedDays: number;
}

/**
 * Uma obra na comparação físico × financeiro.
 *
 * `timeProgress` é o prazo de contrato decorrido; `executionProgress`, o pago
 * sobre o contratado. A diferença entre os dois é o atraso — negativo quer
 * dizer que o dinheiro anda mais devagar que o calendário.
 */
export interface AnalyticsObraRow {
  readonly id: string;
  readonly name: string;
  readonly contractNumber: string | null;
  readonly status: ObraStatus;
  readonly visitStatus: VisitStatus;
  readonly fiscalName: string | null;
  readonly companyName: string | null;
  readonly contractedCents: number;
  readonly paidCents: number;
  readonly timeProgress: number;
  readonly executionProgress: number;
  /** `executionProgress - timeProgress`. Negativo é obra atrás do calendário. */
  readonly gap: number;
}

export interface AnalyticsFiscalRow {
  readonly id: string;
  readonly name: string;
  readonly obras: number;
  readonly contractedCents: number;
  readonly paidCents: number;
  readonly reports: number;
  readonly overdueVisits: number;
}

export interface AnalyticsCompanyRow {
  readonly id: string;
  readonly name: string;
  readonly obras: number;
  readonly contractedCents: number;
  /** Faturado: soma das medições do período. */
  readonly measuredCents: number;
  readonly glossCents: number;
}

export interface AnalyticsResult {
  readonly totals: AnalyticsTotals;
  readonly flow: AnalyticsFlow;
  readonly months: readonly AnalyticsMonth[];
  readonly deadlines: AnalyticsDeadlines;
  readonly obras: readonly AnalyticsObraRow[];
  readonly fiscais: readonly AnalyticsFiscalRow[];
  readonly companies: readonly AnalyticsCompanyRow[];
}
